import type { AgentFramePlanFoodsCommitted } from "@/clients/streaming/agent-types";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { chatSessionIdAtom } from "./chat.store";

/** A coach turn that announced (via its background note) that it is adding
 *  food to the plan and has not yet landed the refreshed plan. Drives the
 *  temporary "Adding food..." card at the top of My Plan. In-memory only: after
 *  an app restart the card simply does not show for an in-flight add. */
export interface PendingFoodAdd {
  clientTurnId: string;
  startedAt: string;
}

export const pendingFoodAddAtom = atom<PendingFoodAdd | null>(null);

/** Meal ids the coach committed to the plan this session, newest first. My
 *  Plan hoists these to the top of the day so a just-added meal lands where the
 *  temporary card was. In-memory only — a reload falls back to the plan's own
 *  breakfast → snack order, which is accepted until the backend records an
 *  "added at" time per placement. */
export const recentlyAddedMealIdsAtom = atom<string[]>([]);

const RECENTLY_ADDED_LIMIT = 50;

/** New ids first (in the given order), then the previous ids minus duplicates,
 *  capped so the list cannot grow without bound over a long session. */
export const prependMealIds = (current: string[], ids: string[]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const id of [...ids, ...current]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged.slice(0, RECENTLY_ADDED_LIMIT);
};

/** Every meal id the frame wrote to the plan, in frame order, across days. */
export const committedMealIds = (frame: AgentFramePlanFoodsCommitted): string[] =>
  frame.days.flatMap((day) => day.items.map((item) => item.meal_id));

/** Id of the chat message (one "Added to your plan" card per day) that a
 *  plan_foods_committed frame produces. Shared by the chat hook and the badge
 *  store so a badge can always be matched back to its message. */
export const committedCardId = (basketId: string, targetDate: string): string =>
  `plan-foods:${basketId}:${targetDate}`;

/** "Just added" marker on a My Plan meal card: links the meal to the chat
 *  message that added it, within one chat session. Lives as long as that
 *  conversation lives on the device — tapping it, a new chat, the transcript
 *  expiry or logout all remove it. There is no chat-history API yet. */
export interface PlanFoodBadge {
  sessionId: string;
  mealId: string;
  messageId: string;
}

export const planFoodBadgesAtom = atomWithStorage<PlanFoodBadge[]>(
  LocalStorageKeys.CHAT_PLAN_FOOD_BADGES_KEY,
  [],
);

const PLAN_FOOD_BADGES_LIMIT = 100;

/** One badge per session+meal, the newest message id winning; capped. */
export const addPlanFoodBadges = (
  current: PlanFoodBadge[],
  incoming: PlanFoodBadge[],
): PlanFoodBadge[] => {
  const key = (badge: PlanFoodBadge) => `${badge.sessionId}:${badge.mealId}`;
  const seen = new Set<string>();
  const merged: PlanFoodBadge[] = [];
  for (const badge of [...incoming, ...current]) {
    if (!badge.mealId || seen.has(key(badge))) continue;
    seen.add(key(badge));
    merged.push(badge);
  }
  return merged.slice(0, PLAN_FOOD_BADGES_LIMIT);
};

/** Keeps only badges of `sessionId` whose message is still in the transcript. */
export const prunePlanFoodBadges = (
  current: PlanFoodBadge[],
  sessionId: string,
  liveMessageIds: Set<string>,
): PlanFoodBadge[] => {
  const kept = current.filter(
    (badge) => badge.sessionId === sessionId && liveMessageIds.has(badge.messageId),
  );
  return kept.length === current.length ? current : kept;
};

/** A badge for every meal the frame wrote, pointing at that day's card. */
export const badgesFromCommittedFrame = (
  frame: AgentFramePlanFoodsCommitted,
  sessionId: string,
): PlanFoodBadge[] =>
  frame.days.flatMap((day) =>
    day.items.map((item) => ({
      sessionId,
      mealId: item.meal_id,
      messageId: committedCardId(frame.basket_id, day.target_date),
    })),
  );

/** mealId → messageId for the live chat session only. Changes only when a
 *  badge is added/removed or the session changes, not per streamed token. */
export const planFoodBadgeMealsAtom = atom((get) => {
  const sessionId = get(chatSessionIdAtom);
  const map = new Map<string, string>();
  for (const badge of get(planFoodBadgesAtom)) {
    if (badge.sessionId === sessionId && !map.has(badge.mealId)) {
      map.set(badge.mealId, badge.messageId);
    }
  }
  return map;
});
